/**
 * SyncEngine — durable background sync of the IndexedDB queue.
 *
 * Public surface (used by AutoSync + pages):
 *   - enqueueSave(dataType, record)
 *   - enqueueDelete(dataType, recordId)
 *   - drain()                      — try to flush; called on online + on push
 *   - getPendingCount()            — for UI badges
 *   - subscribeStatus(fn)          — UI listens for { online, pending, draining }
 *
 * Design:
 *   - Idempotency key = `${dataType}::${recordId}::${updated_at}` for saves
 *     and `${dataType}::${recordId}::delete::${ts}` for deletes. Same key
 *     can't be queued twice (Dexie unique index enforces).
 *   - Retries: exponential backoff per item via the `attempts` counter; the
 *     poller picks failed items that have aged past the next-attempt window.
 *   - Order preservation: queue is processed by `seq` (insertion order) per
 *     drain call. Drain is single-flight via the `draining` flag.
 *   - Server-side merge already handles field-level conflicts via
 *     `_fields_updated_at` (existing AutoSync behavior). We piggyback on
 *     that — no extra version field needed here yet.
 */
import maidlyDB from './MaidlyDB';
import syncService from './SyncService';

const BACKOFF_BASE_MS = 1500; // 1.5s
const BACKOFF_MAX_MS = 5 * 60 * 1000; // 5 min

function nextAttemptDelay(attempts) {
  const delay = BACKOFF_BASE_MS * Math.pow(2, Math.max(0, attempts - 1));
  return Math.min(delay, BACKOFF_MAX_MS);
}

class SyncEngine {
  constructor() {
    this.draining = false;
    this.subscribers = new Set();
    this.lastDrainAt = 0;
    this.pollTimer = null;

    if (typeof window !== 'undefined') {
      window.addEventListener('online', () => this.drain());
      window.addEventListener('offline', () => this._notify());
      // Periodic drain for failed retries
      this.pollTimer = setInterval(() => this.drain(), 15_000);
    }
  }

  subscribeStatus(fn) {
    this.subscribers.add(fn);
    this._emit(fn);
    return () => this.subscribers.delete(fn);
  }

  async _emit(fn) {
    try {
      const pending = await maidlyDB.queue.count();
      fn({
        online: typeof navigator === 'undefined' ? true : navigator.onLine,
        pending,
        draining: this.draining,
      });
    } catch (e) {
      // _emit is fire-and-forget UI updates — log but don't break callers.
      console.warn('[SyncEngine] subscriber emit failed', e);
    }
  }

  async _notify() {
    const pending = await maidlyDB.queue.count();
    const state = {
      online: typeof navigator === 'undefined' ? true : navigator.onLine,
      pending,
      draining: this.draining,
    };
    this.subscribers.forEach((fn) => {
      try { fn(state); } catch (e) {
        console.warn('[SyncEngine] subscriber notify failed', e);
      }
    });
  }

  async enqueueSave(dataType, record) {
    if (!record || !record.id) return;
    const ts = record._last_updated_at || record.updated_at || new Date().toISOString();
    await maidlyDB.queue.enqueue({
      idempotencyKey: `${dataType}::${record.id}::${ts}`,
      op: 'save',
      dataType,
      recordId: record.id,
      payload: record,
    });
    // Mirror to IndexedDB so the record is durable even before sync flush
    await maidlyDB.records.put(dataType, [record]);
    this._notify();
    // Best-effort immediate drain
    this.drain();
  }

  async enqueueDelete(dataType, recordId) {
    if (!recordId) return;
    const ts = new Date().toISOString();
    await maidlyDB.queue.enqueue({
      idempotencyKey: `${dataType}::${recordId}::delete::${ts}`,
      op: 'delete',
      dataType,
      recordId,
      payload: null,
    });
    await maidlyDB.records.remove(dataType, [recordId]);
    this._notify();
    this.drain();
  }

  async drain() {
    if (this.draining) return;
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      this._notify();
      return;
    }
    if (!syncService.getSyncKey()) {
      // Not signed in yet — drain later
      this._notify();
      return;
    }

    this.draining = true;
    this._notify();
    try {
      // Process in insertion-order, capped per batch
      const items = await maidlyDB.queue.peek(50);
      for (const item of items) {
        // Respect exponential backoff
        if (item.attempts > 0 && item.lastError) {
          const since = Date.now() - Date.parse(item.queuedAt || new Date().toISOString());
          const need = nextAttemptDelay(item.attempts);
          if (since < need) continue;
        }
        await maidlyDB.queue.markInflight(item.seq);
        try {
          if (item.op === 'save') {
            // bulkSave returns { success, results } — treat truthy success as ack
            const res = await syncService.bulkSave({
              [item.dataType]: [item.payload],
            });
            if (res?.success) {
              await maidlyDB.queue.ack(item.seq);
            } else {
              await maidlyDB.queue.fail(item.seq, res?.error || 'bulkSave returned no success');
            }
          } else if (item.op === 'delete') {
            const res = await syncService.delete(item.dataType, item.recordId);
            if (res?.success !== false) {
              await maidlyDB.queue.ack(item.seq);
            } else {
              await maidlyDB.queue.fail(item.seq, res?.error || 'delete failed');
            }
          } else {
            await maidlyDB.queue.ack(item.seq); // unknown op — drop
          }
        } catch (e) {
          await maidlyDB.queue.fail(item.seq, e?.message || String(e));
        }
      }
    } finally {
      this.draining = false;
      this.lastDrainAt = Date.now();
      this._notify();
    }
  }

  async getPendingCount() {
    return maidlyDB.queue.count();
  }
}

const syncEngine = new SyncEngine();
export default syncEngine;
